import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import { Col, HorizontalScroll } from '@paypalcorp/pp-react';
import {
  isLoggedIn,
  isMobileLayout,
  isTabletLayout,
  joinTopicIntents,
} from '../../helpers/twHelper';
import {
  doServerSideFptiCall,
  finalIntentSelected,
  ocpPromptCardSelected,
  searchSheetOpen,
} from './TopicIntents-Actions';
import { generateLinkData, trackActivityEvent, trackClickEvent } from '../../helpers/analytics/fptiHelper';
import { CONTACT_TOPIC_TREE_INTENTS } from '../../common/storeConstants';
import { intents, pageNames } from '../../common/constants';
import { fetchTopicData } from '../ContactOption/ContactOptionsAction';
import { redirectLocation } from '../../../js/contactUs/actions/action-contact-location';
import { FPTI_DATA } from '../../common/propTypes';
import { resetCallUsInfo } from '../callUsComponent/CallUs-Actions';
import { updateContext } from '../../helpers/omniChannel/omniChannelHelper';
import { ocpPromptFPTIs } from '../../helpers/analytics/contactUsPageAnalytics';
import TopicIntentsBlobs from './TopicIntentsBlobs';
import { getEcmArticleFptiTags } from '../../helpers/globalHelper';

const TopicIntents = function (props) {
  const dispatch = useDispatch();
  const {
    priorityIntents,
    isLoading,
    isError,
    searchSheetClose,
    selectedFinalIntent,
    isOcpLoading,
    isOcpEnabled,
    ocp,
    selectedOcpPrompt,
  } = useSelector((state) => state[CONTACT_TOPIC_TREE_INTENTS]);
  const topicTreeIntentCurentState = useSelector((state) => state[CONTACT_TOPIC_TREE_INTENTS]);
  const { FPTIData } = props;

  const [currentPage, setCurrentPage] = useState(1);
  const handlePageChange = (index) => {
    setCurrentPage(index);
  };

  useEffect(() => {
    if (!isOcpLoading) {
      if (Array.isArray(priorityIntents) && !_isEmpty(priorityIntents)) {
        const joinedIntents = joinTopicIntents(priorityIntents);
        trackActivityEvent({
          ...FPTIData.topicTreeIntentsSuccess,
          link: generateLinkData(FPTIData.topicTreeIntentsSuccess.link, { intents: joinedIntents }),
        });
      } else if (isError) {
        trackActivityEvent(FPTIData.topicTreeIntentsError);
      }
    }
  }, [priorityIntents]);

  // When search sheet is opened - from "something else" option
  const ocpOverflowClickHandler = () => {
    trackClickEvent(ocpPromptFPTIs.ocpPromptOverflowClicked);

    if (searchSheetClose) {
      dispatch(searchSheetOpen());
    }
  };

  // When search sheet is opened - from "search" option
  const searchSheetOnClickHandler = () => {
    trackClickEvent({ ...FPTIData.topicTreeSearchOptionClick });

    if (searchSheetClose) {
      dispatch(searchSheetOpen());
    }
  };

  const priorityIntentClickHandler = (e, selectedPriorityIntent) => {
    e.preventDefault();
    if (selectedPriorityIntent && !_isEmpty(selectedPriorityIntent)) {
      const { ref_id: intentCode, topic_id: topicId } = selectedPriorityIntent;
      const fptiClickData = {
        ...FPTIData.topicTreePriorityIntentClick,
        ...{
          link: generateLinkData(FPTIData.topicTreePriorityIntentClick.link, { intentCode }),
        },
      };
      trackClickEvent(fptiClickData);
      // priority intent selected action
      dispatch(finalIntentSelected(selectedPriorityIntent));
      // fire redirect action to contactUs page
      dispatch(redirectLocation({ pageName: pageNames.CONTACT_US }));
      // fetch topic data action includes [fetch article action, fetch IDI channels action]
      dispatch(fetchTopicData(topicId));
      // Chatbot service deprecated - removed postChatBotEligibility call
      // reset call us info
      dispatch(resetCallUsInfo());
    }
  };

  const ocpPromptClickHandler = (e, selectedOcpPromptIntent) => {
    e.preventDefault();

    // OPC prompt click FPTI
    const fptiClickData = {
      ...ocpPromptFPTIs.ocpPromptClicked,
      ...{
        link: generateLinkData(ocpPromptFPTIs.ocpPromptClicked.link, {
          ocpIntent: selectedOcpPromptIntent?.intentCode,
        }),
      },
    };
    trackClickEvent(fptiClickData);

    // get the OCP article details for the selected OCP Intent
    const selectedOcpArticle = ocp.intentDetails.find((ocpCard) => {
      return selectedOcpPromptIntent.intentCode === _get(ocpCard, 'channelDetails.intentCode', '');
    });

    if (selectedOcpArticle) {
      doServerSideFptiCall(selectedOcpArticle);
      topicTreeIntentCurentState.selectedOcpPrompt = selectedOcpArticle;
      dispatch(ocpPromptCardSelected(topicTreeIntentCurentState));
      // get the ECM API tags from article data
      const { fptiDocId, fptiComponentIds } = getEcmArticleFptiTags(selectedOcpArticle);
      // update-context call
      const updateContextObj = {
        articleContent: selectedOcpArticle,
        icfId: '',
        customerConfirmed: true,
        fptiDocId,
        fptiComponentIds,
        pageName: pageNames.CONTACT_US,
      };
      updateContext(updateContextObj);
    }
  };

  const ocpItemsList = () => {
    const intent = ocp.intentDetails.map((ocpCard) => {
      const ocpIntent = _get(ocpCard, 'channelDetails', '');
      ocpIntent.ref_id = ocpIntent.intentCode;
      ocpIntent.id = ocpIntent.intentCode;
      return ocpIntent;
    });

    if (intent.length > 0) {
      intent.push({ id: intents.OVERFLOW, ref_id: intents.OVERFLOW });
    }

    return intent;
  };

  // eslint-disable-next-line react/function-component-definition
  const ocpItemCard = (intent) => {
    if (intent && !_isEmpty(intent)) {
      if (intent.ref_id === intents.OVERFLOW) {
        return (
          <Col key={intents.OVERFLOW}>
            <TopicIntentsBlobs intent={intent} onClickHandler={ocpOverflowClickHandler} />
          </Col>
        );
      }

      return (
        <Col key={intent.ref_id}>
          <TopicIntentsBlobs intent={intent} onClickHandler={ocpPromptClickHandler} />
        </Col>
      );
    }

    return null;
  };

  const sortPriorityIntents = useCallback(() => {
    const sortedTopicIntents = priorityIntents
      .map((intent) => {
        return {
          ...intent,
          id: intent.ref_id,
        };
      })
      .sort((a, b) => a.priority - b.priority);
    sortedTopicIntents.unshift({ id: intents.SEARCH, ref_id: intents.SEARCH });
    return sortedTopicIntents;
  }, [priorityIntents]);

  // eslint-disable-next-line react/function-component-definition
  const HorizontalItemCardEle = (intent) => {
    if (intent && !_isEmpty(intent)) {
      if (intent.ref_id === intents.SEARCH) {
        return (
          <Col key={intent.ref_id}>
            <TopicIntentsBlobs intent={intent} onClickHandler={searchSheetOnClickHandler} />
          </Col>
        );
      }
      return (
        <Col key={intent.ref_id}>
          <TopicIntentsBlobs intent={intent} onClickHandler={priorityIntentClickHandler} />
        </Col>
      );
    }
    return null;
  };

  const renderOCPIntents = (numOfVisibleItems = 7) => {
    // FPTI - OCP Prompt shown
    trackActivityEvent({ ...ocpPromptFPTIs.ocpPromptShown });

    return (
      <div className="sh_topic_intent__ocp_intents" data-testid="sh-topic-intent-ocp-intents">
        <HorizontalScroll
          items={ocpItemsList()}
          renderItem={ocpItemCard}
          numOfVisibleItems={numOfVisibleItems}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    );
  };

  const renderMobileOrTabletPriorityIntents = (numOfVisibleItems) => {
    return (
      <div className="sh_topic_intent__priority_intents" data-testid="sh-topic-intent-priority-intents">
        <HorizontalScroll
          items={sortPriorityIntents()}
          renderItem={HorizontalItemCardEle}
          numOfVisibleItems={numOfVisibleItems}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    );
  };

  const renderDesktopPriorityIntent = (intent) => {
    if (intent.ref_id === intents.SEARCH) {
      return (
        <Col
          key={intent.ref_id}
          className="sh_topic_intent__desktop_priority_intent"
          data-testid="sh-topic-intent-desktop-priority-intent"
        >
          <TopicIntentsBlobs intent={intent} onClickHandler={searchSheetOnClickHandler} desktopGrid />
        </Col>
      );
    }
    return (
      <Col
        key={intent.ref_id}
        className="sh_topic_intent__desktop_priority_intent"
        data-testid="sh-topic-intent-desktop-priority-intent"
      >
        <TopicIntentsBlobs intent={intent} onClickHandler={priorityIntentClickHandler} desktopGrid />
      </Col>
    );
  };

  const renderDesktopPriorityIntents = () => {
    const sortedPriorityIntents = sortPriorityIntents();
    return sortedPriorityIntents.map((intent) => renderDesktopPriorityIntent(intent));
  };

  const renderDesktopIntentsGrid = () => {
    return (
      <div className="sh_topic_intent__desktop_priority_intents" data-testid="sh-topic-intent-ocp-intents">
        {renderDesktopPriorityIntents()}
      </div>
    );
  };

  const renderMobileOrTabletIntents = (numOfVisibleItems = 3) => {
    if (isLoading) return null;

    if (!isLoading && Array.isArray(priorityIntents) && !_isEmpty(priorityIntents) && _isEmpty(selectedFinalIntent)) {
      /**
       * When User is logged in && OCP intents are available then show the ocp intents and 'Something else' option
       * Selecting 'Something else' would show the topic intents sheet
       */
      if (isLoggedIn() && isOcpEnabled && _isEmpty(selectedOcpPrompt)) {
        return renderOCPIntents(numOfVisibleItems);
      }

      // render the priority Intents
      if (!isLoggedIn() || (!isOcpLoading && !isOcpEnabled)) {
        return renderMobileOrTabletPriorityIntents(numOfVisibleItems);
      }
    }

    return null;
  };

  const renderDesktopIntents = () => {
    if (isLoading) return null;

    if (!isLoading && Array.isArray(priorityIntents) && !_isEmpty(priorityIntents) && _isEmpty(selectedFinalIntent)) {
      /**
       * When User is logged in && OCP intents are available then show the ocp intents and 'Something else' option
       * Selecting 'Something else' would show the topic intents sheet
       */
      if (isLoggedIn() && isOcpEnabled && _isEmpty(selectedOcpPrompt)) {
        return renderOCPIntents();
      }

      // render the priority Intents
      if (!isLoggedIn() || (!isOcpLoading && !isOcpEnabled)) {
        return renderDesktopIntentsGrid();
      }
    }

    return null;
  };

  if (isMobileLayout()) {
    return renderMobileOrTabletIntents();
  }

  if (isTabletLayout()) {
    return renderMobileOrTabletIntents(7);
  }

  return renderDesktopIntents();
};

TopicIntents.propTypes = {
  pageName: PropTypes.string.isRequired,
  FPTIData: PropTypes.shape(FPTI_DATA),
};

export default TopicIntents;
